import React, { useState, useRef, useCallback, useEffect, useId } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  alpha
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useAuth } from '../context/AuthContext';
import { AssetUploadKind, uploadAssetToR2 } from '../services/r2AssetUpload';
import {
  UPLOAD_CONFIG,
  validateFile,
  BATCH_UPLOAD_CONFIG,
  ASSET_LIMITS,
  PROJECT_CONFIG
} from '../config/uploadConfig';
import { formatBytes, pluralize } from '../utils/format';
import { brand } from '../theme';
import AssetEffectsManager, { AssetEffect } from './AssetEffectsManager';

export interface UploadedFile {
  id: string;
  url: string;
  name: string;
  size: number;
  type: string;
  startTime?: number;
  endTime?: number;
  position?: string;
  description?: string;
  effects?: AssetEffect[];
  /**
   * When the file finished uploading (ms since 1970). A brief file not yet in
   * a project is deleted 7 days after upload, so a restored draft drops it.
   */
  uploadedAt?: number;
}

type MediaKind = 'image' | 'video' | 'audio' | 'other';

/**
 * Decide how to preview a file.
 *
 * This used to regex the URL for a trailing extension — /\.(png|jpg)$/ — but
 * every stored asset URL ends in ?token=<signature>, so no pattern ever
 * matched and EVERY asset showed "Preview not available for this file type".
 * Prefer the MIME type we already know, and fall back to the URL's path with
 * the query string removed.
 */
const classifyMedia = (url: string, mimeType?: string): MediaKind => {
  const mime = (mimeType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';

  let path = url;
  try {
    path = new URL(url, window.location.origin).pathname;
  } catch {
    path = url.split(/[?#]/)[0];
  }
  if (/\.(jpe?g|png|gif|webp|svg)$/i.test(path)) return 'image';
  if (/\.(mp4|webm|ogv|mov|m4v)$/i.test(path)) return 'video';
  if (/\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(path)) return 'audio';
  return 'other';
};

/** ".jpg, .jpeg, .png" -> "JPG or PNG". */
export const formatExtensions = (extensions: string[]): string => {
  const names = Array.from(new Set(
    extensions.map((ext) => ext.replace(/^\./, '').toUpperCase()).map((name) => (name === 'JPEG' ? 'JPG' : name))
  ));
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
};

const NOUNS: Record<UnifiedFileUploadProps['fileType'], { one: string; many: string }> = {
  image: { one: 'image', many: 'images' },
  audio: { one: 'audio file', many: 'audio files' },
  video: { one: 'video', many: 'videos' },
};

/** Stable default, so a missing prop never looks like a new list. */
const NO_FILES: UploadedFile[] = [];

interface UnifiedFileUploadProps {
  fileType: 'image' | 'audio' | 'video';
  onFilesUploaded: (files: UploadedFile[]) => void;
  multiple?: boolean;
  maxFiles?: number;
  showTimeline?: boolean;
  showPosition?: boolean;
  existingFiles?: UploadedFile[];
  /**
   * Reports how many files are still transferring, so the parent can hold
   * submission until they land instead of silently submitting without them.
   */
  onUploadingChange?: (inFlightCount: number) => void;
}

interface FileUploadItem {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  url?: string;
  /** When the upload finished; the details dialog may attach it later. */
  uploadedAt?: number;
  preview?: string;
  startTime?: number;
  endTime?: number;
  position?: string;
  description?: string;
}

const newId =() => `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const FileTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const sx = { fontSize: 22, color: 'text.secondary' };
  if (type.startsWith('image/')) return <ImageOutlinedIcon sx={sx} />;
  if (type.startsWith('audio/')) return <AudioFileOutlinedIcon sx={sx} />;
  if (type.startsWith('video/')) return <VideoFileOutlinedIcon sx={sx} />;
  return <InsertDriveFileOutlinedIcon sx={sx} />;
};

const thumbSx = {
  width: 48,
  height: 48,
  flexShrink: 0,
  borderRadius: '6px',
  border: `1px solid ${brand.border}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'grey.100',
  overflow: 'hidden',
} as const;

const UnifiedFileUpload: React.FC<UnifiedFileUploadProps> = ({
  fileType,
  onFilesUploaded,
  multiple = false,
  maxFiles,
  showTimeline = true,
  showPosition = false,
  existingFiles = NO_FILES,
  onUploadingChange
}) => {
  // Use asset-specific limits from config
  const getAssetLimits = (type: string) => {
    switch (type) {
      case 'image':
        return ASSET_LIMITS.images;
      case 'audio':
        return ASSET_LIMITS.audio;
      default:
        return null;
    }
  };

  const assetLimits = getAssetLimits(fileType);
  const effectiveMaxFiles = maxFiles || assetLimits?.maxCount || BATCH_UPLOAD_CONFIG.maxFiles;
  const nouns = NOUNS[fileType];
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const idBase = useId();

  const [uploadItems, setUploadItems] = useState<FileUploadItem[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(existingFiles);
  const [notice, setNotice] = useState<string>('');

  // Synchronous mirror of the committed list. Several uploads finish in the
  // same tick, and each one used to build its next list from the captured
  // `uploadedFiles` of its own render — so parallel uploads overwrote each
  // other and the parent only ever received the last file. Updating this ref
  // before setState means every commit sees the real current list.
  const committedFilesRef = useRef<UploadedFile[]>(existingFiles);
  // The list last handed to the parent. When `existingFiles` comes back as
  // anything else, the parent changed it (a restored draft), so follow it -
  // reading the prop only once left restored files invisible here, and the
  // next upload replaced them in the brief.
  const lastReportedRef = useRef<UploadedFile[]>(existingFiles);

  const commitFiles = useCallback(
    (updater: (prev: UploadedFile[]) => UploadedFile[]) => {
      const next = updater(committedFilesRef.current);
      committedFilesRef.current = next;
      lastReportedRef.current = next;
      setUploadedFiles(next);
      onFilesUploaded(next);
      return next;
    },
    [onFilesUploaded]
  );

  useEffect(() => {
    if (existingFiles === lastReportedRef.current) return;
    lastReportedRef.current = existingFiles;
    committedFilesRef.current = existingFiles;
    setUploadedFiles(existingFiles);
  }, [existingFiles]);

  // One controller per transfer, so removing a file stops its upload instead
  // of letting it finish and attach itself to the brief anyway.
  const controllersRef = useRef(new Map<string, AbortController>());
  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  const [isDragging, setIsDragging] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<MediaKind>('other');

  // Uploads waiting for the user to fill in their details, worked one at a time.
  const [pendingDetailIds, setPendingDetailIds] = useState<string[]>([]);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewName, setPreviewName] = useState<string>('');

  const config = UPLOAD_CONFIG[fileType];
  const totalLimit = PROJECT_CONFIG.maxTotalAssetsSize;
  const perFileLimit = Math.min(config.maxSize, totalLimit);

  // Handle file selection
  const handleFileSelect = (files: FileList | null) => {
    if (!files || !user) return;

    const fileArray = Array.from(files);
    const remainingSlots = Math.max(0, effectiveMaxFiles - uploadItems.length - uploadedFiles.length);
    const filesToProcess = fileArray.slice(0, remainingSlots);
    const skipped = fileArray.length - filesToProcess.length;
    setNotice(
      skipped > 0
        ? `${pluralize(skipped, 'file')} not added: you can add up to ${pluralize(effectiveMaxFiles, nouns.one, nouns.many)} here.`
        : ''
    );

    // Calculate current total size from existing files
    const currentTotalSize = uploadedFiles.reduce((sum, file) => sum + file.size, 0) +
                           uploadItems.reduce((sum, item) => sum + item.file.size, 0);

    const newUploadItems: FileUploadItem[] = [];
    let newFilesTotalSize = 0;

    filesToProcess.forEach(file => {
      const validation = validateFile(file, fileType);

      // Check if adding this file would exceed the project's asset limit
      if (validation.valid && currentTotalSize + newFilesTotalSize + file.size > totalLimit) {
        newUploadItems.push({
          id: newId(),
          file,
          progress: 0,
          status: 'error',
          error: `This would take these ${nouns.many} past the ${formatBytes(totalLimit)} limit (${formatBytes(currentTotalSize + newFilesTotalSize)} already added).`
        });
      } else if (validation.valid) {
        const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
        newUploadItems.push({
          id: newId(),
          file,
          progress: 0,
          status: 'pending',
          preview
        });
        newFilesTotalSize += file.size;
      } else {
        // Show error for invalid file
        newUploadItems.push({
          id: newId(),
          file,
          progress: 0,
          status: 'error',
          error: validation.error
        });
      }
    });

    setUploadItems(prev => [...prev, ...newUploadItems]);

    // Auto-start upload for valid files
    newUploadItems
      .filter(item => item.status === 'pending')
      .forEach(item => uploadFile(item));
  };

  // Upload the file directly to R2. The API only authorizes and finalizes the
  // object, so large media never passes through the Worker request body.
  const uploadFile = async (item: FileUploadItem) => {
    if (!user) return;

    const controller = new AbortController();
    controllersRef.current.set(item.id, controller);

    // Update status to uploading
    setUploadItems(prev => prev.map(i =>
      i.id === item.id ? { ...i, status: 'uploading' as const } : i
    ));

    try {
      const token = await user.getIdToken(true);
      if (controller.signal.aborted) return;
      const kind: AssetUploadKind = fileType === 'video' ? 'reference-video' : fileType;
      const stored = await uploadAssetToR2(item.file, kind, token, (progress) => {
        setUploadItems(prev => prev.map(i =>
          i.id === item.id ? { ...i, progress } : i
        ));
      }, controller.signal);
      // Removed while it was transferring: don't attach it.
      if (controller.signal.aborted) return;
      setUploadItems(prev => prev.map(i =>
        i.id === item.id
          ? { ...i, status: 'success' as const, url: stored.url, progress: 100, uploadedAt: Date.now() }
          : i
      ));

      if (!showTimeline && !showPosition) {
        const uploadedFile: UploadedFile = {
          id: item.id,
          url: stored.url,
          name: item.file.name,
          size: item.file.size,
          type: item.file.type,
          uploadedAt: Date.now(),
        };
        commitFiles(prev => [...prev, uploadedFile]);
        setUploadItems(prev => prev.filter(i => i.id !== item.id));
      } else {
        // Queue it. Several uploads finish independently, and each one used to
        // set itself as the dialog's target — so dropping five files bound the
        // dialog to whichever finished last and silently dropped the other
        // four from the brief, each still showing a green tick.
        setPendingDetailIds(prev => (prev.includes(item.id) ? prev : [...prev, item.id]));
      }
    } catch (error: any) {
      if (controller.signal.aborted || error?.name === 'AbortError') return;
      console.error('R2 upload error:', error);
      setUploadItems(prev => prev.map(i =>
        i.id === item.id
          ? { ...i, status: 'error' as const, error: error?.message || 'The upload failed. Try again.' }
          : i
      ));
    } finally {
      controllersRef.current.delete(item.id);
    }
  };

  const totalFiles = uploadItems.length + uploadedFiles.length;
  const canUploadMore = totalFiles < effectiveMaxFiles;

  const openFilePicker = () => {
    if (canUploadMore) fileInputRef.current?.click();
  };

  // Handle drag and drop
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Not memoised: handleFileSelect closes over current state (uploadItems,
  // uploadedFiles, limits), and an empty dep array froze this handler on the
  // first render's copy — so dropped files were validated against stale state.
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!canUploadMore) return;
    handleFileSelect(e.dataTransfer.files);
  };

  /**
   * Attach the item currently in the dialog to the brief.
   *
   * `keepDetails: false` is the Skip path — the file was still uploaded and
   * the user saw it finish, so dropping it silently (the old behaviour)
   * meant the asset never reached the brief while appearing to have done so.
   * Skip attaches the file without the optional details.
   */
  const attachPendingItem = (keepDetails: boolean) => {
    const item = uploadItems.find(i => i.id === selectedItemId);
    const doneId = selectedItemId;

    if (item && item.url) {
      const uploadedFile: UploadedFile = {
        id: item.id,
        url: item.url,
        name: item.file.name,
        size: item.file.size,
        type: item.file.type,
        uploadedAt: item.uploadedAt ?? Date.now(),
        ...(keepDetails
          ? {
              startTime: item.startTime,
              endTime: item.endTime,
              position: item.position,
              description: item.description,
            }
          : {}),
      };
      commitFiles(prev => [...prev, uploadedFile]);
      setUploadItems(prev => prev.filter(i => i.id !== doneId));
    }

    setDetailsDialogOpen(false);
    setSelectedItemId(null);
    setPendingDetailIds(prev => prev.filter(id => id !== doneId));
  };

  const handleSaveDetails = () => attachPendingItem(true);

  // Tell the parent how many transfers are still in flight.
  const inFlightCount = uploadItems.filter(i => i.status === 'pending' || i.status === 'uploading').length;
  useEffect(() => {
    onUploadingChange?.(inFlightCount);
  }, [inFlightCount, onUploadingChange]);
  useEffect(() => () => onUploadingChange?.(0), [onUploadingChange]);

  // Work the queue one file at a time so each upload gets its own dialog.
  useEffect(() => {
    if (detailsDialogOpen || pendingDetailIds.length === 0) return;
    const nextId = pendingDetailIds[0];
    if (!uploadItems.some(i => i.id === nextId)) {
      setPendingDetailIds(prev => prev.filter(id => id !== nextId));
      return;
    }
    setSelectedItemId(nextId);
    setDetailsDialogOpen(true);
  }, [pendingDetailIds, detailsDialogOpen, uploadItems]);

  // Remove uploaded file
  const handleRemoveFile = (fileId: string) => {
    commitFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Remove an upload in progress (cancelling it) or a failed one
  const handleRemoveItem = (itemId: string) => {
    controllersRef.current.get(itemId)?.abort();
    controllersRef.current.delete(itemId);
    const item = uploadItems.find(i => i.id === itemId);
    if (item?.preview) {
      URL.revokeObjectURL(item.preview);
    }
    setUploadItems(prev => prev.filter(i => i.id !== itemId));
    setPendingDetailIds(prev => prev.filter(id => id !== itemId));
  };

  // Preview file
  const handlePreview = (file: UploadedFile) => {
    setPreviewUrl(file.url);
    setPreviewName(file.name);
    setPreviewKind(classifyMedia(file.url, file.type));
    setPreviewDialogOpen(true);
  };

  const updateFile = (fileId: string, changes: Partial<UploadedFile>) => {
    commitFiles(prev => prev.map(f => (f.id === fileId ? { ...f, ...changes } : f)));
  };

  const updateItem = (changes: Partial<FileUploadItem>) => {
    setUploadItems(prev => prev.map(i => (i.id === selectedItemId ? { ...i, ...changes } : i)));
  };

  const selectedItem = uploadItems.find(i => i.id === selectedItemId);
  // Only images and audio carry timing, placement and instructions into the
  // brief; reference videos are sent as files alone, so they get no fields.
  const showDetails = showTimeline || showPosition;
  const maxSeconds = assetLimits?.maxDuration || 300;

  const limitsText = [
    formatExtensions(config.allowedExtensions),
    perFileLimit >= totalLimit
      ? `up to ${formatBytes(totalLimit)} in total`
      : `up to ${formatBytes(perFileLimit)} each`,
    multiple ? `${totalFiles} of ${effectiveMaxFiles} added` : null,
  ].filter(Boolean).join(' · ');

  const primaryId = `${idBase}-dropzone-label`;
  const limitsId = `${idBase}-dropzone-limits`;
  const detailsTitleId = `${idBase}-details-title`;
  const previewTitleId = `${idBase}-preview-title`;

  return (
    <Box>
      {/* Drop zone: a real button, so Tab reaches it and Enter or Space opens the picker. */}
      <Box
        ref={dropZoneRef}
        role="button"
        tabIndex={canUploadMore ? 0 : -1}
        aria-disabled={!canUploadMore || undefined}
        aria-labelledby={primaryId}
        aria-describedby={limitsId}
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFilePicker();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        data-dragging={isDragging ? 'true' : undefined}
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          border: `1px dashed ${isDragging ? brand.copper : brand.borderStrong}`,
          borderRadius: 2,
          backgroundColor: isDragging ? alpha(brand.copper, 0.04) : 'grey.50',
          textAlign: 'center',
          cursor: canUploadMore ? 'pointer' : 'not-allowed',
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
          '&:hover': canUploadMore ? { borderColor: brand.textSubtle } : undefined,
          '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: 2 },
        }}
      >
        <CloudUploadOutlinedIcon aria-hidden="true" sx={{ fontSize: 28, color: 'text.secondary' }} />
        <Typography id={primaryId} sx={{ fontWeight: 600, mt: 0.75, fontSize: '0.9375rem' }}>
          {!canUploadMore ? (
            `You've added the most ${nouns.many} you can here`
          ) : isDragging ? (
            `Drop to add ${multiple ? nouns.many : `the ${nouns.one}`}`
          ) : (
            <>
              Drop {multiple ? nouns.many : `a ${nouns.one}`} here or{' '}
              <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                choose {multiple ? 'files' : 'a file'}
              </Box>
            </>
          )}
        </Typography>
        <Typography id={limitsId} variant="body2" color="text.secondary" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
          {limitsText}
        </Typography>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={config.allowedTypes.join(',')}
        multiple={multiple}
        onChange={(e) => {
          handleFileSelect(e.target.files);
          // Let the same file be chosen again after it was removed.
          e.target.value = '';
        }}
        data-testid={`upload-input-${fileType}`}
      />

      {notice && (
        <Typography variant="body2" color="error" role="alert" sx={{ mt: 1 }}>
          {notice}
        </Typography>
      )}

      {/* Transfers in progress, and files that failed */}
      {uploadItems.length > 0 && (
        <Box component="ul" aria-label={`Uploading ${nouns.many}`} sx={{ listStyle: 'none', p: 0, m: 0, mt: 2 }}>
          {uploadItems.map((item) => {
            const inFlight = item.status === 'pending' || item.status === 'uploading';
            const statusText = item.status === 'pending'
              ? 'Waiting to upload'
              : item.status === 'uploading'
              ? `Uploading · ${item.progress}%`
              : item.status === 'success'
              ? 'Uploaded · add the details to attach it'
              : "Couldn't upload";
            return (
              <Box
                component="li"
                key={item.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}
              >
                <Box sx={thumbSx}>
                  {item.preview ? (
                    <Box component="img" src={item.preview} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <FileTypeIcon type={item.file.type} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {item.file.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: item.status === 'error' ? 'error.main' : 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatBytes(item.file.size)} · {statusText}
                  </Typography>
                  {item.status === 'uploading' && (
                    <LinearProgress
                      variant="determinate"
                      value={item.progress}
                      aria-label={`Uploading ${item.file.name}`}
                      sx={{ mt: 0.75, height: 4, borderRadius: 2, backgroundColor: 'grey.200' }}
                    />
                  )}
                  {item.error && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
                      {item.error}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveItem(item.id)}
                  aria-label={inFlight ? `Cancel upload of ${item.file.name}` : `Remove ${item.file.name}`}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Files attached to the brief */}
      {uploadedFiles.length > 0 && (
        <Box component="ul" aria-label={`Added ${nouns.many}`} sx={{ listStyle: 'none', p: 0, m: 0, mt: 2, borderTop: 1, borderColor: 'divider' }}>
          {uploadedFiles.map((file) => (
            <Box component="li" key={file.id} sx={{ py: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={thumbSx}>
                  {file.type.startsWith('image/') ? (
                    <Box component="img" src={file.url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <FileTypeIcon type={file.type} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatBytes(file.size)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Button size="small" onClick={() => handlePreview(file)} aria-label={`Preview ${file.name}`}>
                    Preview
                  </Button>
                  <Button size="small" onClick={() => handleRemoveFile(file.id)} aria-label={`Remove ${file.name}`} sx={{ color: 'text.secondary' }}>
                    Remove
                  </Button>
                </Box>
              </Box>

              {showDetails && (
                <Box sx={{ mt: 1.75, pl: { sm: 7.5 } }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: showPosition && file.type.startsWith('image/') ? '120px 120px minmax(0, 1fr)' : '120px 120px',
                      },
                      gap: 1.5,
                    }}
                  >
                    {showTimeline && (
                      <>
                        <TextField
                          size="small"
                          label="Start (s)"
                          type="number"
                          value={file.startTime || ''}
                          onChange={(e) => updateFile(file.id, { startTime: parseFloat(e.target.value) || 0 })}
                          inputProps={{ min: 0, max: maxSeconds, step: 0.1, 'aria-label': `Start time in seconds for ${file.name}` }}
                        />
                        <TextField
                          size="small"
                          label="End (s)"
                          type="number"
                          value={file.endTime || ''}
                          onChange={(e) => updateFile(file.id, { endTime: parseFloat(e.target.value) || 0 })}
                          inputProps={{ min: 0, max: maxSeconds, step: 0.1, 'aria-label': `End time in seconds for ${file.name}` }}
                        />
                      </>
                    )}
                    {showPosition && file.type.startsWith('image/') && (
                      <TextField
                        size="small"
                        label="Position"
                        value={file.position || ''}
                        onChange={(e) => updateFile(file.id, { position: e.target.value })}
                        placeholder="e.g. Top right, centre"
                        sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                      />
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Instructions for your editor"
                    multiline
                    minRows={2}
                    value={file.description || ''}
                    onChange={(e) => updateFile(file.id, { description: e.target.value })}
                    placeholder={ASSET_LIMITS.notes.placeholder}
                    inputProps={{ maxLength: ASSET_LIMITS.notes.maxLength }}
                    helperText={`${(file.description || '').length} of ${ASSET_LIMITS.notes.maxLength} characters`}
                    sx={{ mt: 1.5 }}
                  />

                  {(fileType === 'image' || fileType === 'audio') && (
                    <AssetEffectsManager
                      assetId={file.id}
                      assetType={fileType as 'image' | 'audio'}
                      assetName={file.name}
                      effects={file.effects || []}
                      onEffectsChange={(effects) => updateFile(file.id, { effects })}
                    />
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Details for a file that just finished uploading */}
      <Dialog
        open={detailsDialogOpen}
        // Closing without choosing attaches the file without details: it is
        // already uploaded, and leaving it out of the brief would be a surprise.
        onClose={() => attachPendingItem(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby={detailsTitleId}
      >
        <DialogTitle id={detailsTitleId} sx={{ overflowWrap: 'anywhere' }}>
          Details for {selectedItem?.file.name || `this ${nouns.one}`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Optional. Tell your editor when {fileType === 'audio' ? 'it should play' : 'it should appear'}
            {showPosition && fileType === 'image' ? ' and where' : ''}. You can change this later.
          </DialogContentText>
          {showTimeline && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, mt: 1 }}>
              <TextField
                label="Start (seconds)"
                type="number"
                margin="dense"
                value={selectedItem?.startTime || ''}
                onChange={(e) => updateItem({ startTime: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, max: maxSeconds, step: 0.1 }}
              />
              <TextField
                label="End (seconds)"
                type="number"
                margin="dense"
                value={selectedItem?.endTime || ''}
                onChange={(e) => updateItem({ endTime: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, max: maxSeconds, step: 0.1 }}
              />
            </Box>
          )}

          {showPosition && fileType === 'image' && (
            <TextField
              fullWidth
              label="Position"
              margin="dense"
              placeholder="e.g. Top right, centre"
              value={selectedItem?.position || ''}
              onChange={(e) => updateItem({ position: e.target.value })}
            />
          )}

          <TextField
            fullWidth
            label="Instructions for your editor"
            margin="dense"
            multiline
            minRows={2}
            value={selectedItem?.description || ''}
            onChange={(e) => updateItem({ description: e.target.value })}
            inputProps={{ maxLength: ASSET_LIMITS.notes.maxLength }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => attachPendingItem(false)}>Skip</Button>
          <Button onClick={handleSaveDetails} variant="contained">Save details</Button>
        </DialogActions>
      </Dialog>

      {/* Preview */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby={previewTitleId}
      >
        <DialogTitle id={previewTitleId} sx={{ pr: 7, overflowWrap: 'anywhere' }}>
          {previewName || 'Preview'}
          <IconButton
            aria-label="Close preview"
            onClick={() => setPreviewDialogOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewUrl && (
            <Box sx={{ textAlign: 'center' }}>
              {previewKind === 'image' ? (
                <Box component="img" src={previewUrl} alt={previewName} sx={{ maxWidth: '100%', maxHeight: 500 }} />
              ) : previewKind === 'video' ? (
                <Box component="video" controls src={previewUrl} sx={{ maxWidth: '100%', maxHeight: 500, backgroundColor: brand.ink }} />
              ) : previewKind === 'audio' ? (
                <Box component="audio" controls src={previewUrl} sx={{ width: '100%' }} />
              ) : (
                <Box sx={{ p: 4 }}>
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    This file can't be previewed here.
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{ mt: 2 }}
                    onClick={() => window.open(previewUrl, '_blank', 'noopener')}
                  >
                    Open in a new tab
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UnifiedFileUpload;
